// BM-H398 - the fee earner's itemised review summary, derived on read.
//
// Fetched from GET /matters/{matterRef}/derived-summary/{fid}. Nothing here
// is stored; the same module that stamps `unresolved_items_count` on the
// review task computes this list. The severance fee is a chargeable item in
// its own block, never a disbursement.

import { useEffect, useState } from "react";

import { clientFormsApi } from "../../api/clientForms";
import { errorMessage } from "../../utils/errorMessage";
import { RELATIONSHIP_STATUS_OPTIONS } from "./wills/S1Testator";
import { STRUCTURE_OPTIONS } from "./wills/S5Structure";

const panel = { border: "1px solid #e2e8f0", borderRadius: 12, padding: 16, background: "#fff", marginBottom: 20 };
const h2 = { fontSize: 16, marginTop: 0, marginBottom: 12, color: "#0f172a" };
const li = { fontSize: 13, color: "#0f172a", marginBottom: 4 };

// BM-H4277 (13 Oct 2026): the tier signals printed as raw JSON on Liezel's 1562 review
// ({"couple_mode":"single","chosen_structure":"A_absolute_then_children",...}). Each one now reads as a
// labelled answer, in the words the client saw on the form.
const label = (options, v) => (options.find((o) => o.value === v) || {}).label;
const yesNo = (v) => (v === true ? "Yes" : v === false ? "No" : "Not answered");

export function tierSignalRows(t) {
  const sig = t || {};
  const mode = { single: "Single Will", mirror: "Mirror Wills (two clients)" }[sig.couple_mode];
  const holdings = Number(sig.business_holdings) || 0;
  return [
    ["Wills", mode || "Not answered"],
    ["Structure", label(STRUCTURE_OPTIONS, sig.chosen_structure) || "Not chosen yet"],
    ["Business holdings", holdings === 0 ? "None" : String(holdings)],
    ["Agricultural property", yesNo(sig.agricultural_property)],
    ["Domiciled in England and Wales", yesNo(sig.eng_wal_domiciled)],
    ["Overseas assets", yesNo(sig.has_overseas_assets)],
    ["Relationship status", label(RELATIONSHIP_STATUS_OPTIONS, sig.relationship_status) || "Not answered"],
  ];
}

function Block({ title, testid, children }) {
  return (
    <div data-testid={testid} style={{ marginBottom: 14 }}>
      <h3 style={{ fontSize: 13, margin: "0 0 6px", color: "#334155", textTransform: "uppercase", letterSpacing: 0.4 }}>{title}</h3>
      {children}
    </div>
  );
}

export default function DerivedSummaryPanel({ matterRef, formInstanceId, engineKind }) {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!formInstanceId || (engineKind || "wills") !== "wills") return;
    let cancelled = false;
    setSummary(null);
    setError(null);
    clientFormsApi.getDerivedSummary(matterRef, formInstanceId)
      .then(({ data }) => { if (!cancelled) setSummary(data); })
      .catch((err) => { if (!cancelled) setError(errorMessage(err, "Could not load the review summary.")); });
    return () => { cancelled = true; };
  }, [matterRef, formInstanceId, engineKind]);

  if ((engineKind || "wills") !== "wills") return null;
  if (error) return <section data-testid="derived-summary-error" style={panel}>{error}</section>;
  if (!summary) return <section data-testid="derived-summary-loading" style={panel}>Loading review summary...</section>;

  const sev = summary.severance || { items: [] };

  return (
    <section data-testid="derived-summary" style={panel}>
      <h2 style={h2}>
        Items to resolve <span data-testid="derived-summary-count" style={{ fontWeight: 400, color: "#475569" }}>({summary.unresolved_items_count})</span>
      </h2>

      {summary.mirror_split && (
        <Block title="Mirror split" testid="derived-mirror-split">
          <p style={li}>{summary.mirror_split.text} Sections: {summary.mirror_split.diverged_sections.join(", ")}.</p>
        </Block>
      )}

      {summary.partner_capacity_gate?.length > 0 && (
        <Block title="Second client, before we start" testid="derived-partner-gate">
          <ul style={{ paddingLeft: 18, margin: 0 }}>{summary.partner_capacity_gate.map((x) => <li key={x.field} style={li}>{x.text}</li>)}</ul>
        </Block>
      )}

      <Block title={`Not known answers (${summary.not_known.length})`} testid="derived-not-known">
        {summary.not_known.length === 0 ? <p style={li}>None.</p> : (
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            {summary.not_known.map((x, i) => <li key={i} style={li} data-testid={`derived-not-known-${x.section}-${x.field}`}>{x.testator === "B" ? "Second client: " : ""}{x.section}.{x.field}</li>)}
          </ul>
        )}
      </Block>

      <Block title={`Business follow ups (${summary.business_follow_ups.length})`} testid="derived-business">
        {summary.business_follow_ups.length === 0 ? <p style={li}>None.</p> : (
          <ul style={{ paddingLeft: 18, margin: 0 }}>{summary.business_follow_ups.map((x, i) => <li key={i} style={li}>{x.text}</li>)}</ul>
        )}
      </Block>

      <Block title="Severance of joint tenancy" testid="derived-severance">
        {sev.items.length === 0 ? <p style={li}>Not elected.</p> : (
          <>
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              {sev.items.map((x, i) => <li key={i} style={li}>{x.text}{x.fee_earner_confirmed ? " (confirmed)" : " (awaiting your confirmation)"}</li>)}
            </ul>
            {sev.fee_line && (
              <p data-testid="derived-severance-fee" style={{ ...li, marginTop: 8, fontWeight: 600 }}>
                Chargeable item: {sev.fee_line.label}, {"\u00a3"}{sev.fee_line.fee_gbp} {sev.fee_line.vat}. Not attached to any quote by the questionnaire.
              </p>
            )}
          </>
        )}
      </Block>

      {summary.first_executor_divergence && (
        <Block title="First executor" testid="derived-first-executor">
          <p style={li}>{summary.first_executor_divergence.text} ({summary.first_executor_divergence.a_first_executor} / {summary.first_executor_divergence.b_first_executor})</p>
        </Block>
      )}

      <Block title="Tier signals (captured, not priced)" testid="derived-tier-signals">
        <ul style={{ paddingLeft: 18, margin: 0 }}>
          {tierSignalRows(summary.tier_signals).map(([k, v]) => <li key={k} style={li}>{k}: {v}</li>)}
        </ul>
      </Block>
    </section>
  );
}
